import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import { 
  FileSpreadsheet, 
  ArrowRight,
  Loader2,
  Check,
  Upload,
  Zap,
  Users,
  Mail,
  Video,
  Linkedin,
  Calendar,
  Building2,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  hubspot: Building2,
  salesforce: Users,
  mailchimp: Mail,
  video: Video,
  linkedin: Linkedin,
  calendar: Calendar,
};

const TemplatesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prebuilt-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (templateId) => {
    if (!user) {
      // Store selected template in localStorage and redirect to login
      localStorage.setItem('pending_template', templateId);
      toast.info('Please sign in to use templates');
      navigate('/login');
      return;
    }
    
    // Navigate to upload page with template param
    navigate(`/upload?template=${templateId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/pricing/subscription" className="text-slate-600 hover:text-slate-900">
                Pricing
              </Link>
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="outline">Sign in</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Pre-built Templates
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-slate-900 mb-4">
            Cleaning Templates
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Select a template, upload your file, and get perfectly clean data ready for import. 
            We&apos;ll automatically detect columns and apply the right cleaning rules.
          </p>
        </div>

        {/* Templates Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const IconComponent = iconMap[template.icon] || Building2;
            
            return (
              <div 
                key={template.id}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-indigo-200 transition-all group"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${template.color}20` }}
                >
                  <IconComponent 
                    className="w-7 h-7" 
                    style={{ color: template.color }}
                  />
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {template.name}
                </h3>
                
                <p className="text-slate-600 text-sm mb-4">
                  {template.description}
                </p>
                
                {/* Columns/Rules Preview */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Cleans these fields:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(template.recommended_rules || {}).slice(0, 5).map((field) => (
                      <span 
                        key={field}
                        className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                      >
                        {field.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {Object.keys(template.recommended_rules || {}).length > 5 && (
                      <span className="px-2 py-1 text-slate-400 text-xs">
                        +{Object.keys(template.recommended_rules).length - 5} more
                      </span>
                    )}
                  </div>
                </div>
                
                <Button
                  onClick={() => handleUseTemplate(template.id)}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white group-hover:bg-indigo-600"
                  data-testid={`use-template-${template.id}`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Use This Template
                </Button>
              </div>
            );
          })}
        </div>

        {/* How It Works */}
        <div className="mt-16 bg-white rounded-2xl border border-slate-200 p-8">
          <h2 className="text-2xl font-heading font-bold text-slate-900 text-center mb-8">
            How Templates Work
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">1</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Select Template</h3>
              <p className="text-sm text-slate-600">
                Choose a template that matches your data source (HubSpot, Salesforce, etc.)
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">2</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Upload Your File</h3>
              <p className="text-sm text-slate-600">
                We&apos;ll auto-detect columns and map them to the template fields
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-indigo-600">3</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Download Clean Data</h3>
              <p className="text-sm text-slate-600">
                Review the suggested rules, clean your data, and export
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">
            Don&apos;t see a template you need? You can still clean any file manually.
          </p>
          <Link to={user ? "/upload" : "/login"}>
            <Button variant="outline" size="lg">
              Upload Any File
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </main>
      
      <SupportButton />
    </div>
  );
};

export default TemplatesPage;
